import React from 'react';
import type { ReactNode } from 'react';

import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import type { DocumentStatusFilterType } from 'src/components/reports/report-detail/GridStatusCell';

import { formatBytes } from './helpers/utils/formatBytes';
import { RepositoryDropzoneOverlay } from './RepositoryDropzoneOverlay';
import { RepositoryDropzoneUploadProgress } from './RepositoryDropzoneUploadProgress';
import { useRepositoryDropzone } from './useRepositoryDropzone';

interface RepositoryDropzoneProps {
  children: ReactNode;
  documentStatusFilter: DocumentStatusFilterType;
  repositoryId: UUID;
  className?: string;
  disabled?: boolean;
  onDocumentsRestored?: (documentIds: readonly UUID[]) => void;
  onDocumentsTrashed?: (documentIds: readonly UUID[]) => void;
}

export const RepositoryDropzone = React.memo(function RepositoryDropzone({
  children,
  className,
  disabled = false,
  documentStatusFilter,
  onDocumentsRestored,
  onDocumentsTrashed,
  repositoryId,
}: RepositoryDropzoneProps): JSX.Element {
  const {
    cancelActiveUploads,
    cancelLargeUpload,
    closePanel,
    confirmLargeUpload,
    deleteUpload,
    dialog,
    dropzoneProps,
    isDragging,
    isPanelOpen,
    isUploadPaused,
    pauseActiveUploads,
    pendingUpload,
    restoreUpload,
    resumeActiveUploads,
    retryUpload,
    uploadFiles,
  } = useRepositoryDropzone({
    disabled,
    documentStatusFilter,
    onDocumentsRestored,
    onDocumentsTrashed,
    repositoryId,
  });

  return (
    <>
      <div className={className} {...dropzoneProps}>
        <RepositoryDropzoneOverlay isDragging={isDragging} />
        {children}
        {isPanelOpen && (
          <RepositoryDropzoneUploadProgress
            isUploadPaused={isUploadPaused}
            uploadFiles={uploadFiles}
            onCancelActiveUploads={cancelActiveUploads}
            onClose={closePanel}
            onDeleteFile={deleteUpload}
            onPauseUploads={pauseActiveUploads}
            onRestoreFile={restoreUpload}
            onResumeUploads={resumeActiveUploads}
            onRetryFile={retryUpload}
          />
        )}
      </div>
      {dialog}
      <ConfirmationDialog
        message={`You are about to upload ${pendingUpload?.files.length ?? 0} files (${formatBytes(pendingUpload?.totalSize ?? 0)}). This may take a while. Do you want to continue?`}
        open={pendingUpload !== null}
        primaryText="Upload"
        secondaryText="Cancel"
        title="Large Upload"
        variant={ConfirmationDialogVariant.Info}
        onClickPrimary={confirmLargeUpload}
        onClose={cancelLargeUpload}
      />
    </>
  );
});
